import { createEntityAdapter } from '@reduxjs/toolkit';
import { EMPTY_ARRAY } from 'constants/defaults';
import { xor } from 'lodash-es';
import { DateTime } from 'luxon';
import { normalize } from 'normalizr';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import { appsListSchema } from 'schema/apps';
import type { AppAccount } from 'types/apps';
import type { DeepReadonly } from 'types/utility';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import type { State } from './apps.types';
import * as actionTypes from './apps.types';
import { withXhrStatusReducer } from './apps.types';

export const importEntityAdapter = createEntityAdapter<actionTypes.ImportEntity>();

export const initialState: State = {
  appsEntities: {},
  appsResult: [],
  accounts: {},
  subscriptions: {},
  draft: {
    options: null,
    subscriptions: [],
  },
  import: {
    status: actionTypes.ImportStatus.IDLE,
    entities: importEntityAdapter.getInitialState(),
    selectedEntities: EMPTY_ARRAY,
    jobId: null,
  },
  filter: {
    search: '',
  },
  showAppsListModal: false,
  appEditorId: null,
  appAccountEditorId: null,
  xhrStatus: xhrStatusFactory(),
};

const reducer = reducerWithInitialState<DeepReadonly<State>>(initialState)
  .case(actionTypes.fetchedApps, (state, payload) => {
    const { entities, result } = normalize(payload, appsListSchema);

    if (!entities.apps) {
      return state;
    }

    return {
      ...state,
      appsEntities: entities.apps,
      appsResult: result,
    };
  })
  .case(actionTypes.removeApp, (state, payload) => {
    const appId = payload;
    const app = state.appsEntities[appId];

    return {
      ...state,
      appsEntities: {
        ...state.appsEntities,
        [appId]: {
          ...app,
          integrated: false,
          connected_integrations_count: 0,
          disconnected_integrations_count: 0,
        },
      },
    };
  })
  .case(actionTypes.fetchedAccounts, (state, payload) => {
    const { appId } = payload;
    const accounts = payload.accounts.length ? { [appId]: payload.accounts } : {};

    return {
      ...state,
      accounts: {
        ...state.accounts,
        ...accounts,
      },
    };
  })
  .case(actionTypes.addAccount, (state, payload) => {
    const { appId, account } = payload;
    const app = state.appsEntities[appId];

    return {
      ...state,
      appsEntities: {
        ...state.appsEntities,
        [appId]: {
          ...app,
          integrated: true,
          connected_integrations_count: app.connected_integrations_count + 1,
        },
      },
      accounts: {
        ...state.accounts,
        [appId]: [...state.accounts[appId], account],
      },
    };
  })
  .case(actionTypes.removeAccount, (state, payload) => {
    const { appId, accountId, disconnected } = payload;
    const app = state.appsEntities[appId];
    const {
      connected_integrations_count: connectedCount,
      disconnected_integrations_count: disconnectedCount,
    } = app;

    const filteredAccounts = state.accounts[appId].filter(account => account.id !== accountId);

    const accounts = filteredAccounts.length
      ? {
          ...state.accounts,
          [appId]: state.accounts[appId].filter(account => account.id !== accountId),
        }
      : {};

    return {
      ...state,
      appsEntities: {
        ...state.appsEntities,
        [appId]: {
          ...app,
          connected_integrations_count: disconnected ? connectedCount : connectedCount - 1,
          disconnected_integrations_count: disconnected ? disconnectedCount - 1 : disconnectedCount,
        },
      },
      accounts,
    };
  })
  .case(actionTypes.fetchedSubscriptions, (state, payload) => {
    const { accountId, subscriptions } = payload;

    return {
      ...state,
      subscriptions: {
        ...state.subscriptions,
        [accountId]: subscriptions,
      },
    };
  })
  .case(actionTypes.updateSubscriptions, (state, payload) => {
    const { accountId, subscriptions } = payload;

    return {
      ...state,
      subscriptions: {
        ...state.subscriptions,
        [accountId]: subscriptions,
      },
    };
  })
  .case(actionTypes.updateDraft, (state, payload) => {
    return {
      ...state,
      draft: {
        ...state.draft,
        ...payload,
      },
    };
  })
  .case(actionTypes.updateDraftSubscription, (state, payload) => {
    const { id, value } = payload;
    const { subscriptions } = state.draft;

    return {
      ...state,
      draft: {
        ...state.draft,
        subscriptions: (subscriptions || []).map(subscription => {
          if (subscription.object_id !== id) {
            return subscription;
          }

          return {
            ...subscription,
            ...value,
          };
        }),
      },
    };
  })
  .case(actionTypes.updateDraftOptions, (state, payload) => {
    const newOptions =
      typeof payload === 'string'
        ? payload
        : {
            ...((state.draft.options ?? {}) as any),
            ...((payload ?? {}) as any),
          };

    return {
      ...state,
      draft: {
        ...state.draft,
        options: newOptions,
      },
    };
  })
  .case(actionTypes.toggleDraftSubscriptionIntegrated, (state, payload) => {
    const { id } = payload;
    const { subscriptions } = state.draft;

    return {
      ...state,
      draft: {
        ...state.draft,
        subscriptions: (subscriptions || []).map(subscription => {
          if (subscription.object_id !== id) {
            return subscription;
          }

          // Sets a value or toggles the property
          return {
            ...subscription,
            integrated: !subscription.integrated,
          };
        }),
      },
    };
  })
  .case(actionTypes.updateSearchFilter, (state, payload) => {
    return {
      ...state,
      filter: {
        ...state.filter,
        search: payload,
      },
    };
  })
  .case(actionTypes.toggleAppsListModal, state => {
    return {
      ...state,
      showAppsListModal: !state.showAppsListModal,
    };
  })
  .case(actionTypes.toggleAppEditor, (state, payload) => {
    if (typeof payload === 'string') {
      const app = Object.values(state.appsEntities).find(app => app.id === payload);
      if (!app) return state;

      return {
        ...state,
        appEditorId: app.app_id,
      };
    }

    // Close
    return {
      ...state,
      appEditorId: payload,
      appAccountEditorId: payload === null ? null : state.appAccountEditorId,
    };
  })
  .case(actionTypes.toggleAccountEditor, (state, payload) => {
    return {
      ...state,
      appAccountEditorId: payload || null,
    };
  })
  .case(actionTypes.updateAccount, (state, action) => {
    const index = state.accounts[action.appId]?.findIndex(
      account => account.id === action.appAccountId
    );
    if (index == null || index === -1) {
      return state;
    }

    const updatedAccount = {
      ...state.accounts[action.appId][index],
      ...action.patch,
    };

    const updatedAccounts = state.accounts[action.appId].slice();
    updatedAccounts.splice(index, 1, updatedAccount);

    return {
      ...state,
      accounts: {
        ...state.accounts,
        [action.appId]: updatedAccounts,
      },
    };
  })
  .case(actionTypes.setImportStatusFinished, (state, { appId, appAccountId }) => {
    const appAccountIndex = state.accounts[appId].findIndex(
      appAccount => appAccount.id === appAccountId
    );
    if (appAccountIndex === -1) {
      return state;
    }

    const timestamp = DateTime.utc().toISO();

    const updatedAccount: AppAccount = {
      ...state.accounts[appId][appAccountIndex],
      last_imported_at: timestamp,
    };

    if (!updatedAccount.first_imported_at) {
      updatedAccount.first_imported_at = timestamp;
    }

    const updatedAccounts = state.accounts[appId].slice();
    updatedAccounts.splice(appAccountIndex, 1, updatedAccount);

    return {
      ...state,
      accounts: {
        ...state.accounts,
        [appId]: updatedAccounts,
      },
    };
  })
  .case(actionTypes.initImportWizard, (state, payload) => ({
    ...state,
    import: {
      ...payload,
      status: actionTypes.ImportStatus.INITIALIZED,
      entities: importEntityAdapter.setAll((state as State).import.entities, payload.entities),
      selectedEntities: payload.entities.map(e => e.id),
    },
  }))
  .case(actionTypes.resetImportWizard, state => ({
    ...state,
    import: initialState.import,
  }))
  .case(actionTypes.setImportWizardStatus, (state, status) => {
    return { ...state, import: { ...state.import, status } };
  })
  .case(actionTypes.toggleSelectedEntityIds, (state, payload) => {
    const selectedEntities = xor(state.import.selectedEntities, payload);
    return {
      ...state,
      import: {
        ...state.import,
        selectedEntities: selectedEntities,
      },
    };
  });

export default withXhrStatusReducer(reducer);
